import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './DiscountCodeBank.css';

interface DiscountCode {
  codeId: string;
  code: string;
  discountId: string;
  discountName: string;
  discountType: string;
  discountValue: number;
  codeType: 'single_use' | 'multi_use' | 'unlimited';
  maxUses: number;
  currentUses: number;
  validFrom: string;
  validUntil: string;
  timezone: string;
  distributedVia?: string;
  postId?: string;
  campaignId?: string;
  campaignName?: string;
  distributedAt?: string;
  restaurantSpecific: boolean;
  minPurchaseAmount?: number;
  maxDiscountAmount?: number;
  applicableMenuItems: string[];
  isActive: boolean;
  isRedeemed: boolean;
  createdAt: string;
}

interface BaseDiscount {
  discountId: string;
  name: string;
  discountType: string;
  value: number;
  isActive: boolean;
}

interface Campaign {
  campaignId: string;
  name: string;
  status: string;
}

interface CodeGenerationRequest {
  discountId: string;
  quantity: number;
  codePrefix: string;
  codeLength: number;
  codeType: 'single_use' | 'multi_use' | 'unlimited';
  maxUses: number;
  validFrom: string;
  validUntil: string;
  campaignId?: string;
  minPurchaseAmount?: number;
  maxDiscountAmount?: number;
  restaurantSpecific: boolean;
}

export const DiscountCodeBank: React.FC = () => {
  const { getIdToken } = useAuth();
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [discounts, setDiscounts] = useState<BaseDiscount[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDiscount, setFilterDiscount] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  const [generateRequest, setGenerateRequest] = useState<CodeGenerationRequest>({
    discountId: '',
    quantity: 1,
    codePrefix: 'PROMO',
    codeLength: 8,
    codeType: 'single_use',
    maxUses: 1,
    validFrom: '',
    validUntil: '',
    campaignId: '',
    minPurchaseAmount: 0,
    maxDiscountAmount: 0,
    restaurantSpecific: true
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      fetchDiscountCodes();
    }
  }, [selectedStore]);

  const fetchInitialData = async () => {
    try {
      const token = await getIdToken();
      
      // Fetch stores
      const storesResponse = await fetch(API_ENDPOINTS.stores(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (storesResponse.ok) {
        const storesData = await storesResponse.json();
        setStores(storesData.data || []);
        if (storesData.data?.length > 0) {
          setSelectedStore(storesData.data[0].storeId);
        }
      }

      // Fetch existing discounts (from the working discount system)
      try {
        const discountsResponse = await fetch(API_ENDPOINTS.discountCodes(), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (discountsResponse.ok) {
          const discountsData = await discountsResponse.json();
          setDiscounts(discountsData.data || []);
        }
      } catch (err) {
        console.log('Using mock discount data');
        // Mock discount data for development
        setDiscounts([
          {
            discountId: 'disc_001',
            name: '15% Off Appetizers',
            discountType: 'percentage',
            value: 15,
            isActive: true
          },
          {
            discountId: 'disc_002',
            name: '$5 Off Entrees',
            discountType: 'fixed_amount',
            value: 5,
            isActive: true
          },
          {
            discountId: 'disc_003',
            name: 'Buy One Get One Free',
            discountType: 'bogo',
            value: 100,
            isActive: true
          }
        ]);
      }

      // Fetch campaigns
      const campaignsResponse = await fetch(API_ENDPOINTS.socialMediaCampaigns(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (campaignsResponse.ok) {
        const campaignsData = await campaignsResponse.json();
        setCampaigns(campaignsData.data || []);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    }
  };

  const fetchDiscountCodes = async () => {
    if (!selectedStore) return;
    
    setLoading(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.discountCodes()}?storeId=${selectedStore}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        // Mock data for development
        setCodes([
          {
            codeId: 'code_001',
            code: 'SPRING2024FB',
            discountId: 'disc_001',
            discountName: '15% Off Appetizers',
            discountType: 'percentage',
            discountValue: 15,
            codeType: 'single_use',
            maxUses: 1,
            currentUses: 0,
            validFrom: new Date().toISOString(),
            validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            timezone: 'America/New_York',
            distributedVia: 'facebook',
            campaignName: 'Spring Social Media Campaign',
            restaurantSpecific: true,
            minPurchaseAmount: 20,
            applicableMenuItems: [],
            isActive: true,
            isRedeemed: false,
            createdAt: new Date().toISOString()
          },
          {
            codeId: 'code_002',
            code: 'INSTA5OFF',
            discountId: 'disc_002',
            discountName: '$5 Off Entrees',
            discountType: 'fixed_amount',
            discountValue: 5,
            codeType: 'multi_use',
            maxUses: 100,
            currentUses: 23,
            validFrom: new Date().toISOString(),
            validUntil: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            timezone: 'America/New_York',
            distributedVia: 'instagram',
            restaurantSpecific: true,
            minPurchaseAmount: 25,
            applicableMenuItems: [],
            isActive: true,
            isRedeemed: false,
            createdAt: new Date().toISOString()
          }
        ]);
        setError(null);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setCodes(data.data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch discount codes');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCodes = async () => {
    if (!selectedStore || !generateRequest.discountId) {
      setError('Please select a discount and fill in all required fields');
      return;
    }

    if (!generateRequest.validFrom || !generateRequest.validUntil) {
      setError('Please set valid date range for the codes');
      return;
    }

    try {
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.generateDiscountCodes(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...generateRequest,
          storeId: selectedStore
        })
      });

      if (!response.ok) throw new Error('Failed to generate discount codes');
      
      await fetchDiscountCodes();
      setShowGenerateModal(false);
      
      // Reset form
      setGenerateRequest({
        discountId: '',
        quantity: 1,
        codePrefix: 'PROMO',
        codeLength: 8,
        codeType: 'single_use',
        maxUses: 1,
        validFrom: '',
        validUntil: '',
        campaignId: '',
        minPurchaseAmount: 0,
        maxDiscountAmount: 0,
        restaurantSpecific: true
      });
      
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate codes');
    }
  };

  const handleDeactivateCode = async (codeId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this code? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.discountCode(codeId)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: false })
      });

      if (!response.ok) throw new Error('Failed to deactivate code');
      
      await fetchDiscountCodes();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate code');
    }
  };

  const handleBulkExport = () => {
    const csvContent = [
      ['Code', 'Discount', 'Type', 'Value', 'Valid From', 'Valid Until', 'Uses', 'Status', 'Campaign'].join(','),
      ...filteredCodes.map(code => [
        code.code,
        code.discountName,
        code.discountType,
        code.discountValue,
        new Date(code.validFrom).toLocaleDateString(),
        new Date(code.validUntil).toLocaleDateString(),
        `${code.currentUses}/${code.maxUses}`,
        code.isActive ? 'Active' : 'Inactive',
        code.campaignName || 'None'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `discount-codes-${selectedStore}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredCodes = codes.filter(code => {
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'active' && code.isActive) ||
      (filterStatus === 'inactive' && !code.isActive) ||
      (filterStatus === 'used' && code.currentUses >= code.maxUses) ||
      (filterStatus === 'expired' && new Date(code.validUntil) < new Date());
    
    const discountMatch = filterDiscount === 'all' || code.discountId === filterDiscount;
    
    const searchMatch = searchTerm === '' || 
      code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.discountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (code.campaignName && code.campaignName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return statusMatch && discountMatch && searchMatch;
  });

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const getStatusBadge = (code: DiscountCode) => {
    if (!code.isActive) return <span className="status-badge inactive">Inactive</span>;
    if (code.currentUses >= code.maxUses) return <span className="status-badge used">Fully Used</span>;
    if (new Date(code.validUntil) < new Date()) return <span className="status-badge expired">Expired</span>;
    return <span className="status-badge active">Active</span>;
  };

  const getUsageProgress = (code: DiscountCode) => {
    if (code.codeType === 'unlimited') return '∞';
    const percentage = (code.currentUses / code.maxUses) * 100;
    return (
      <div className="usage-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${Math.min(percentage, 100)}%` }}
          ></div>
        </div>
        <span className="usage-text">{code.currentUses}/{code.maxUses}</span>
      </div>
    );
  };

  if (loading && !codes.length) {
    return <div className="discount-bank-loading">Loading discount codes...</div>;
  }

  return (
    <div className="discount-code-bank">
      <div className="bank-header">
        <h1>Discount Code Bank</h1>
        
        <div className="header-actions">
          <div className="store-selector">
            <select 
              value={selectedStore} 
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              {stores.map(store => (
                <option key={store.storeId} value={store.storeId}>
                  {store.name} - {store.address?.city}
                </option>
              ))}
            </select>
          </div>
          
          <button 
            className="generate-btn"
            onClick={() => setShowGenerateModal(true)}
          >
            Generate Codes
          </button>
          
          <button 
            className="export-btn"
            onClick={handleBulkExport}
            disabled={filteredCodes.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>[ERROR] {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="filters-section">
        <div className="search-group">
          <input
            type="text"
            placeholder="Search codes, discounts, or campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="used">Fully Used</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Discount:</label>
          <select value={filterDiscount} onChange={(e) => setFilterDiscount(e.target.value)}>
            <option value="all">All Discounts</option>
            {discounts.map(discount => (
              <option key={discount.discountId} value={discount.discountId}>
                {discount.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="results-count">
          {filteredCodes.length} code{filteredCodes.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="codes-grid">
        {filteredCodes.length === 0 ? (
          <div className="no-codes">
            <p>No discount codes found. Generate your first batch of codes to get started!</p>
            <button onClick={() => setShowGenerateModal(true)}>Generate Codes</button>
          </div>
        ) : (
          filteredCodes.map(code => (
            <div key={code.codeId} className="code-card">
              <div className="code-header">
                <div className="code-info">
                  <div className="code-display">{code.code}</div>
                  <div className="discount-info">
                    {code.discountName} - 
                    {code.discountType === 'percentage' ? ` ${code.discountValue}% off` : 
                     code.discountType === 'fixed_amount' ? ` $${code.discountValue} off` :
                     ' BOGO'}
                  </div>
                </div>
                {getStatusBadge(code)}
              </div>

              <div className="code-details">
                <div className="detail-row">
                  <span className="detail-label">Valid:</span>
                  <span className="detail-value">
                    {formatDate(code.validFrom)} - {formatDate(code.validUntil)}
                  </span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Usage:</span>
                  <span className="detail-value">
                    {getUsageProgress(code)}
                  </span>
                </div>

                {code.minPurchaseAmount && code.minPurchaseAmount > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Min Purchase:</span>
                    <span className="detail-value">${code.minPurchaseAmount}</span>
                  </div>
                )}

                {code.distributedVia && (
                  <div className="detail-row">
                    <span className="detail-label">Distributed via:</span>
                    <span className="detail-value">{code.distributedVia}</span>
                  </div>
                )}

                {code.campaignName && (
                  <div className="detail-row">
                    <span className="detail-label">Campaign:</span>
                    <span className="detail-value">{code.campaignName}</span>
                  </div>
                )}
              </div>

              <div className="code-actions">
                <button 
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(code.code);
                    // Could add a toast notification here
                  }}
                  title="Copy code"
                >
                  Copy
                </button>
                
                {code.isActive && (
                  <button 
                    className="deactivate-btn"
                    onClick={() => handleDeactivateCode(code.codeId)}
                    title="Deactivate code"
                  >
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Generate Codes Modal */}
      {showGenerateModal && (
        <div className="modal-overlay">
          <div className="generate-modal">
            <div className="modal-header">
              <h2>Generate Discount Codes</h2>
              <button onClick={() => setShowGenerateModal(false)}>×</button>
            </div>

            <div className="modal-content">
              <div className="form-row">
                <div className="form-group">
                  <label>Discount *</label>
                  <select
                    value={generateRequest.discountId}
                    onChange={(e) => setGenerateRequest({...generateRequest, discountId: e.target.value})}
                    required
                  >
                    <option value="">Select a discount</option>
                    {discounts.filter(d => d.isActive).map(discount => (
                      <option key={discount.discountId} value={discount.discountId}>
                        {discount.name} - 
                        {discount.discountType === 'percentage' ? ` ${discount.value}% off` : 
                         discount.discountType === 'fixed_amount' ? ` $${discount.value} off` :
                         ' BOGO'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={generateRequest.quantity}
                    onChange={(e) => setGenerateRequest({...generateRequest, quantity: parseInt(e.target.value) || 1})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Code Prefix</label>
                  <input
                    type="text"
                    value={generateRequest.codePrefix}
                    onChange={(e) => setGenerateRequest({...generateRequest, codePrefix: e.target.value.toUpperCase()})}
                    placeholder="PROMO"
                    maxLength={10}
                  />
                </div>

                <div className="form-group">
                  <label>Code Length</label>
                  <select
                    value={generateRequest.codeLength}
                    onChange={(e) => setGenerateRequest({...generateRequest, codeLength: parseInt(e.target.value)})}
                  >
                    <option value="6">6 characters</option>
                    <option value="8">8 characters</option>
                    <option value="10">10 characters</option>
                    <option value="12">12 characters</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Code Type *</label>
                  <select
                    value={generateRequest.codeType}
                    onChange={(e) => setGenerateRequest({
                      ...generateRequest, 
                      codeType: e.target.value as 'single_use' | 'multi_use' | 'unlimited',
                      maxUses: e.target.value === 'single_use' ? 1 : generateRequest.maxUses
                    })}
                  >
                    <option value="single_use">Single Use</option>
                    <option value="multi_use">Multi Use</option>
                    <option value="unlimited">Unlimited Use</option>
                  </select>
                </div>

                {generateRequest.codeType !== 'unlimited' && (
                  <div className="form-group">
                    <label>Max Uses</label>
                    <input
                      type="number"
                      min={1}
                      value={generateRequest.maxUses}
                      onChange={(e) => setGenerateRequest({...generateRequest, maxUses: parseInt(e.target.value) || 1})}
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Valid From *</label>
                  <input
                    type="datetime-local"
                    value={generateRequest.validFrom}
                    onChange={(e) => setGenerateRequest({...generateRequest, validFrom: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Valid Until *</label>
                  <input
                    type="datetime-local"
                    value={generateRequest.validUntil}
                    onChange={(e) => setGenerateRequest({...generateRequest, validUntil: e.target.value})}
                    required
                  />
                </div>
              </div>

              {campaigns.length > 0 && (
                <div className="form-group">
                  <label>Campaign (optional)</label>
                  <select
                    value={generateRequest.campaignId || ''}
                    onChange={(e) => setGenerateRequest({...generateRequest, campaignId: e.target.value})}
                  >
                    <option value="">No campaign</option>
                    {campaigns.filter(c => c.status === 'active').map(campaign => (
                      <option key={campaign.campaignId} value={campaign.campaignId}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Min Purchase Amount ($)</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={generateRequest.minPurchaseAmount || ''}
                    onChange={(e) => setGenerateRequest({...generateRequest, minPurchaseAmount: parseFloat(e.target.value) || 0})}
                  />
                </div>

                <div className="form-group">
                  <label>Max Discount Amount ($)</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={generateRequest.maxDiscountAmount || ''}
                    onChange={(e) => setGenerateRequest({...generateRequest, maxDiscountAmount: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={generateRequest.restaurantSpecific}
                    onChange={(e) => setGenerateRequest({...generateRequest, restaurantSpecific: e.target.checked})}
                  />
                  Restaurant-specific (can only be redeemed at this location)
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowGenerateModal(false)}>Cancel</button>
              <button 
                onClick={handleGenerateCodes}
                className="generate-action-btn"
                disabled={!generateRequest.discountId || !generateRequest.validFrom || !generateRequest.validUntil}
              >
                Generate {generateRequest.quantity} Code{generateRequest.quantity !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};